import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  Stack,
} from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { Company, CompanyCreate, CompanyUpdate, uploadCompanyLogo } from '../services/api';

interface CompanyDialogProps {
  open: boolean;
  company: Company | null;
  onClose: () => void;
  onSave: (company: CompanyCreate | CompanyUpdate) => Promise<void>;
}

const CompanyDialog: React.FC<CompanyDialogProps> = ({ open, company, onClose, onSave }) => {
  const [formData, setFormData] = useState<CompanyCreate>({
    name: '',
    code: '',
    ruc: '',
    address: '',
    phone: '',
    email: '',
  });
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (company) {
      setFormData({
        name: company.name,
        code: company.code,
        ruc: company.ruc || '',
        address: company.address || '',
        phone: company.phone || '',
        email: company.email || '',
      });
      setIsActive(company.is_active);
      setPreviewUrl(company.logo_url ? `${process.env.REACT_APP_API_URL?.replace('/api/v1', '')}${company.logo_url}` : '');
    } else {
      setFormData({
        name: '',
        code: '',
        ruc: '',
        address: '',
        phone: '',
        email: '',
      });
      setIsActive(true);
      setPreviewUrl('');
    }
    setSelectedFile(null);
  }, [company, open]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/svg+xml'];
      if (!validTypes.includes(file.type)) {
        alert('Solo se permiten archivos JPEG, PNG y SVG');
        return;
      }

      setSelectedFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadLogo = async () => {
    if (!selectedFile || !company) return;

    setUploading(true);
    try {
      await uploadCompanyLogo(company.id, selectedFile);
      alert('Logo subido exitosamente');
      setSelectedFile(null);
    } catch (error) {
      console.error('Error uploading logo:', error);
      alert('Error al subir el logo');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const dataToSave = company
        ? { ...formData, is_active: isActive }
        : formData;
      await onSave(dataToSave);
      onClose();
    } catch (error) {
      console.error('Error saving company:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{company ? 'Editar Empresa' : 'Nueva Empresa'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            fullWidth
            label="Nombre de la Empresa"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              label="Código"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="RUC"
              value={formData.ruc}
              onChange={(e) => setFormData({ ...formData, ruc: e.target.value })}
            />
          </Box>
          <TextField
            fullWidth
            label="Dirección"
            value={formData.address}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            multiline
            rows={2}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              label="Teléfono"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
          </Box>
          {company && (
            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
              }
              label="Activa"
            />
          )}

          {company && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Logo de la Empresa
              </Typography>
              {previewUrl && (
                <Box sx={{ mb: 2, textAlign: 'center' }}>
                  <img
                    src={previewUrl}
                    alt="Company logo preview"
                    style={{ maxWidth: '200px', maxHeight: '100px', objectFit: 'contain' }}
                  />
                </Box>
              )}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<CloudUploadIcon />}
                  disabled={uploading}
                >
                  Seleccionar Logo
                  <input
                    type="file"
                    hidden
                    accept="image/jpeg,image/png,image/jpg,image/svg+xml"
                    onChange={handleFileChange}
                  />
                </Button>
                {selectedFile && (
                  <Button
                    variant="contained"
                    onClick={handleUploadLogo}
                    disabled={uploading}
                  >
                    {uploading ? 'Subiendo...' : 'Subir Logo'}
                  </Button>
                )}
              </Box>
              {selectedFile && (
                <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                  Archivo seleccionado: {selectedFile.name}
                </Typography>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving || !formData.name || !formData.code}
        >
          {saving ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompanyDialog;
